import { Transition } from '@headlessui/react';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { NavigationEndpoint } from './types';
import { CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME } from './constants';
import GenerateHighlightReelPanel from './GenerateHighlightReelPanel';
import ClipsListItemWithActions from '../ClipsListPage/ClipsListItemWithActions';
import ClipListPreviewSkeleton from '../ClipsListPage/ClipListPreviewSkeleton';
import ContentDialog from '../Sessions/uiComponents/ContentDialog/ContentDialog';
import RenameTitleModal from '../Sessions/AssetsTable/RenameTitleModal';
import ClipBulkDeleteUsageDialog from '../ClipsListPage/ClipBulkDeleteUsageDialog';
import ClipDeletionErrorDialog from '../ClipsListPage/ClipDeletionErrorDialog';
import AssetPopover from './components/AssetPopover';
import PopoverFooter from '@/components/molecules/PopoverCard/PopoverFooter';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import AssetRatingDialog from '@/components/molecules/AssetRating/AssetRatingDialog';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { useHighlightReelPopover } from '@/hooks/useHighlightReelPopover';
import { useReelList } from '@/hooks/useReelList';
import { useAllVisibleReels } from '@/hooks/useAllVisibleClips';
import Button from '@/components/atoms/Button/Button';
import Icon from '@/components/atoms/Icon';
import { core } from '@/stores/core';

type PanelView = 'list' | 'generate';

const HighlightReelPopover = () => {
  const [, setSearchParams] = useSearchParams();
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isHighlightReelPopoverOpen } = useHighlightReelPopover();

  const {
    openDeleteConfirm,
    isConfirmDeleteOpen,
    closeDeleteConfirm,
    onConfirmDelete,
    reelUsageData,
    isReelUsageDialogOpen,
    closeReelUsageDialog,
    reelDeletionError,
    closeReelDeletionErrorDialog,
    setRenamingClip,
    onGenerateSocialPost,
    renamingClip,
    onSaveNewTitle,
    closeRenamingClipDialog,
    duplicateReel
  } = useReelList();

  // SessionDetail owns the fetch and writes reels into the shared clip store; the popover reads
  // the reel slice back out. It attaches to the same query as a disabled observer purely to follow
  // its status — `enabled: false` means it never triggers a fetch of its own, and subscribing (as
  // opposed to reading the cache with getQueryState) is what makes status changes re-render.
  const { allVisibleReels: reels } = useAllVisibleReels();
  const { isFetching } = useQuery<Clip[]>({
    queryKey: ['highlight-reels', broadcastId],
    enabled: false
  });

  // Only a fetch that is in flight with nothing to show counts as loading. Keying off `isFetched`
  // instead strands the skeleton on a session where SessionDetail never enables the query — the
  // Reels nav item is still available there, so the popover has to fall through to the generate
  // view rather than wait forever. The `!reels.length` term is what stops a background refetch
  // blanking reels that are already on screen.
  const isLoadingReels = isFetching && !reels.length;

  const [view, setView] = useState<PanelView>('list');
  // With no reels to show there's nothing to fall back to, so the generate view _is_ the empty state.
  const hasNoReels = !isLoadingReels && !reels.length;
  const activeView: PanelView = hasNoReels ? 'generate' : view;
  // Nothing to go back to when the generate view is standing in for the empty state.
  const canGoBack = activeView === 'generate' && !hasNoReels;

  // Closing the popover only early-returns from render — the component stays mounted, so the view
  // has to be reset explicitly or every later open lands back on the generate view.
  useEffect(() => {
    if (!isHighlightReelPopoverOpen) {
      setView('list');
    }
  }, [isHighlightReelPopoverOpen]);

  const closePopover = useCallback(() => {
    EventBus.dispatch(CustomEvents.HighlightListPopUp, { isOpen: false });
  }, []);

  const onReelSelect = useCallback(
    (clip: Clip) => {
      // Reels live at /highlights — that's what keeps the Reels nav button lit rather than Clips.
      if (!pathname.endsWith(`/${NavigationEndpoint.highlights}`)) {
        navigate(`/${eventId}/${broadcastId}/${NavigationEndpoint.highlights}`);
      }
      setSearchParams({ activeClipId: clip.id });
      if (core.getSnapshot().content?.id !== clip.content.id) {
        core.set(() => ({ content: clip.content }));
      }
      // Close over the EventBus, not this component's local dialog state: SessionDetailLayout keeps
      // its own copy of the open flag to style the nav button, and only the event reaches both.
      closePopover();
    },
    [pathname, navigate, eventId, broadcastId, setSearchParams, closePopover]
  );

  if (!isHighlightReelPopoverOpen) {
    return <></>;
  }

  const isGenerateView = activeView === 'generate';

  return (
    <>
      <AssetPopover
        isOpen
        onClose={closePopover}
        heading={isGenerateView ? 'Generate Highlight Reels' : 'Highlight Reels'}
        closeTrackingId="highlight-reels-close-button"
        onBack={canGoBack ? () => setView('list') : undefined}
        backTrackingId="highlight-reels-back-button"
        headingBadge={
          isGenerateView ? (
            <span
              data-testid="highlight-reels-ai-badge"
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#ffe9d3] py-px pl-2.5 pr-2 text-xs font-medium leading-[18px] text-deep-orange-600"
            >
              <Icon name="IconSparkles" size={14} />
              AI
            </span>
          ) : !isLoadingReels ? (
            <span
              data-testid="highlight-reels-count"
              className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-medium"
            >
              {reels.length}
            </span>
          ) : undefined
        }
        // The generate view brings its own padding and pinned footer. The list drops its top
        // padding so the first reel card sits flush under the header.
        bodyClassName={isGenerateView ? 'p-0' : 'px-2 pb-2'}
        footer={
          isGenerateView ? undefined : (
            <PopoverFooter>
              <Button
                variation="filled"
                buttonSize="full"
                trackingId="generate-highlight-reel-button"
                onClick={() => setView('generate')}
              >
                <Icon name="IconSparkles" size={16} className="mr-2" />
                Generate Highlight Reel
              </Button>
            </PopoverFooter>
          )
        }
      >
        {isGenerateView ? (
          <GenerateHighlightReelPanel
            onCancel={canGoBack ? () => setView('list') : closePopover}
            onGenerated={closePopover}
          />
        ) : (
          <>
            {isLoadingReels && <ClipListPreviewSkeleton count={3} isCollapsed={false} isInPopover />}
            {!isLoadingReels &&
              reels.map((reel: Clip) => (
                <Transition
                  key={reel.asset_metadata.id}
                  show={!reel.isRemoved}
                  leave="transition transform duration-500 delay-100 ease-out"
                  leaveFrom="transform translate-x-0"
                  leaveTo="transform translate-x-[300px]"
                >
                  <ClipsListItemWithActions
                    clip={reel}
                    animateClipList={false}
                    isCollapsed={false}
                    onDelete={openDeleteConfirm}
                    onRenameClip={setRenamingClip}
                    onGenerateSocialPost={onGenerateSocialPost}
                    onDuplicateClip={duplicateReel}
                    onClipSelect={onReelSelect}
                    isInPopover
                    hasCardBackground
                  />
                </Transition>
              ))}
          </>
        )}
      </AssetPopover>
      {renamingClip ? (
        // ContentDialog is generic, so the opt-out class comes from here. The other dialogs below
        // are popover-specific and carry it themselves.
        <ContentDialog
          className={CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}
          isOpen={!!renamingClip}
          setIsOpen={closeRenamingClipDialog}
          title="Rename"
          size="xsmall"
        >
          <RenameTitleModal
            existingTitle={renamingClip.title}
            onSubmit={onSaveNewTitle}
            onClose={closeRenamingClipDialog}
          />
        </ContentDialog>
      ) : null}
      <ConfirmationDialog
        isOpen={isConfirmDeleteOpen}
        title="Delete Highlight Reel"
        content="Are you sure you want to delete this highlight reel? This action cannot be undone."
        confirmLabel="Delete"
        confirmTrackingId="confirm-delete-reel-button"
        cancelTrackingId="cancel-delete-reel-button"
        onClose={closeDeleteConfirm}
        onConfirm={onConfirmDelete}
      />
      <ClipBulkDeleteUsageDialog isOpen={isReelUsageDialogOpen} onClose={closeReelUsageDialog} usages={reelUsageData} />
      <ClipDeletionErrorDialog
        isOpen={!!reelDeletionError}
        onClose={closeReelDeletionErrorDialog}
        error={reelDeletionError}
      />
      <AssetRatingDialog />
    </>
  );
};

export default HighlightReelPopover;
